import { useTranslation } from 'react-i18next'
import { Check } from '@digital-twins/design-system/icons'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import { SOCIAL_UNIVERSE_STEPS } from './socialUniverseForm'

import { UniverseStep } from './socialUniverseForm'

export interface SocialUniverseStepperProps {
  current: UniverseStep
  isReachable: (step: UniverseStep) => boolean
  isComplete: (step: UniverseStep) => boolean
  /** No step can be picked, e.g. while a draft runs. */
  isDisabled?: boolean
  onSelect: (step: UniverseStep) => void
}

/** Numbered steps of the Topic dialog; on phones only the current step keeps its name. */
export function SocialUniverseStepper(props: SocialUniverseStepperProps) {
  const { current, isReachable, isComplete, isDisabled = false, onSelect } = props

  const { t } = useTranslation('app')

  const currentIndex = SOCIAL_UNIVERSE_STEPS.indexOf(current)

  return (
    <nav
      aria-label={t('socialListening.topicDialog.steps')}
      className="flex flex-col gap-2"
      data-testid="social-universe-stepper"
    >
      <ol className="flex items-center gap-1 sm:gap-2">
        {SOCIAL_UNIVERSE_STEPS.map((step, index) => {
          const isCurrent = step === current
          const isDone = !isCurrent && isComplete(step)
          const canSelect = !isDisabled && !isCurrent && isReachable(step)
          const name = t(`socialListening.topicDialog.stepNames.${step}`)
          return (
            <li
              key={step}
              className={cn(
                'flex min-w-0 items-center gap-1 sm:gap-2',
                // Each step grows by the same amount from its own width, so every connector
                // gets the same length and no name is cut while there is room.
                index > 0 ? 'flex-auto' : 'flex-none',
              )}
            >
              {index > 0 ? (
                <span
                  className={cn(
                    'h-px min-w-2 flex-1 sm:min-w-3',
                    index <= currentIndex ? 'bg-foreground/40' : 'bg-border',
                  )}
                  aria-hidden="true"
                />
              ) : null}
              <button
                type="button"
                className={cn(
                  'group inline-flex min-w-0 items-center gap-2 rounded-full py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default sm:pr-1',
                  isCurrent && 'shrink-0',
                )}
                aria-current={isCurrent ? 'step' : undefined}
                aria-label={
                  isDone ? t('socialListening.topicDialog.stepCompleted', { name }) : name
                }
                disabled={!canSelect}
                data-testid={`social-universe-step-${step}`}
                data-state={isCurrent ? 'current' : isDone ? 'complete' : 'upcoming'}
                onClick={() => onSelect(step)}
              >
                <span
                  className={cn(
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs transition-colors',
                    isCurrent && 'border-foreground bg-foreground text-background',
                    isDone && 'border-primary/45 bg-primary/10 text-foreground',
                    !isCurrent && !isDone && 'border-border text-muted-foreground',
                    canSelect && 'group-hover:border-foreground/60',
                  )}
                  aria-hidden="true"
                >
                  {isDone ? <Check className="h-3 w-3" /> : index + 1}
                </span>
                <span
                  className={cn(
                    isCurrent ? 'text-foreground' : 'hidden truncate text-muted-foreground sm:inline',
                    canSelect && 'group-hover:text-foreground',
                  )}
                >
                  {name}
                </span>
              </button>
            </li>
          )
        })}
      </ol>
      <Text
        as="span"
        size="xs"
        color="muted"
        className="sm:sr-only"
        data-testid="social-universe-step-count"
      >
        {t('socialListening.topicDialog.stepCount', {
          current: currentIndex + 1,
          total: SOCIAL_UNIVERSE_STEPS.length,
        })}
      </Text>
    </nav>
  )
}
